<template>
  <div ref="pageEl" class="full-map-page" :class="{ 'full-map-page--embed': embedMode, 'full-map-page--with-table': vehiclePanelExpandedNum !== 3, 'full-map-page--with-drawer': drawerVisible }" v-loading="loading">
    <div ref="mapEl" class="full-map-page__map" />

    <button type="button" class="map-list-toggle" :class="{ 'is-active': vehiclePanelExpandedNum !== 3 }" :aria-expanded="String(vehiclePanelExpandedNum !== 3)" aria-label="展开或收起运单列表" title="运单列表" @click="toggleVehicleList"><i class="el-icon-s-unfold" /></button>

    <div class="map-search-bar">
      <el-select v-model="searchField" class="map-search-bar__select" :clearable="false">
        <el-option label="车牌号" value="vehicleNo" />
        <el-option label="运单号" value="waybillNo" />
      </el-select>
      <el-input v-model.trim="keyword" class="map-search-bar__input" :placeholder="searchField === 'vehicleNo' ? '请输入车牌号' : '请输入运单号'" clearable @keyup.enter.native="applySearch">
        <i slot="suffix" class="el-icon-search map-search-bar__search" @click="applySearch" />
      </el-input>
      <button type="button" class="map-search-bar__filter" :class="{ 'is-active': filterVisible }" title="筛选" @click="filterVisible = !filterVisible"><i class="el-icon-s-operation" /></button>
    </div>

    <div v-if="overviewError" class="map-query-error" role="alert">{{ overviewError }} <el-button type="text" size="mini" @click="applyFilter">重试</el-button></div>

    <div ref="statisticsEl" class="map-statistics">
      <div class="map-statistics__waybills">
        <button class="map-statistics__item" :class="{ 'is-active': statisticFilter === 'pending' }" type="button" aria-label="查看待开始运单" @click="setStatisticFilter('pending')"><i class="el-icon-document" /><span>待开始</span><strong>{{ statistics.pending }}</strong></button>
        <button class="map-statistics__item" :class="{ 'is-active': statisticFilter === 'running' }" type="button" aria-label="查看运输中运单" @click="setStatisticFilter('running')"><i class="el-icon-truck" /><span>运输中</span><strong>{{ statistics.running }}</strong></button>
      </div>
      <div class="map-statistics__vehicles">
        <span><i class="el-icon-truck" />车辆总数 <b>{{ vehicleStatistics.total }}</b></span>
        <span><i class="map-statistics__dot map-statistics__dot--moving" />行驶 <b>{{ vehicleStatistics.moving }}</b></span>
        <span><i class="map-statistics__dot map-statistics__dot--stationary" />静止 <b>{{ vehicleStatistics.stationary }}</b></span>
        <span><i class="map-statistics__dot map-statistics__dot--offline" />离线 <b>{{ vehicleStatistics.offline }}</b></span>
        <span v-if="vehicleStatistics.unknown" class="map-statistics__unknown">未知 {{ vehicleStatistics.unknown }}</span>
      </div>
      <small class="map-statistics__scope">当前筛选范围</small>
    </div>

    <section v-if="!embedMode" ref="alarmBoardEl" class="map-alarm-board">
      <div class="map-alarm-board__actions">
        <button type="button" class="map-alarm-notice" aria-label="展开或收起告警区域" :aria-expanded="String(alarmBoardExpanded)" title="最近告警" @click="toggleAlarmBoard"><i class="el-icon-bell" /><em v-if="alarmCount('ALL') && alarmCount('ALL').unhandled">{{ alarmCount('ALL').unhandled }}</em></button>
        <button v-if="canConfigureAlarms" v-permission="'alarm:config'" type="button" aria-label="预警设置" title="预警设置" @click="openAlarmSetting"><i class="el-icon-setting" /></button>
      </div>
      <div v-show="alarmBoardExpanded" class="map-alarm-board__content" @mouseenter="showRecentAlarms" @mouseleave="alarmListVisible = false">
        <section v-if="alarmListVisible" ref="recentAlarmsEl" class="map-recent-alarms" v-loading="alarmSummaryLoading">
          <header><div><strong>{{ alarmCategoryLabel }}</strong><small>近24小时，最近{{ alarmSummary ? alarmSummary.recentLimit : 20 }}条</small></div><button type="button" aria-label="关闭最近告警" @click="alarmListVisible = false"><i class="el-icon-close" /></button></header>
          <p v-if="alarmSummaryError" class="map-recent-alarms__error">{{ alarmSummaryError }}</p>
          <div v-else class="map-recent-alarms__body">
            <button v-for="alarm in recentAlarms" :key="alarm.alarmNo" type="button" class="map-recent-alarms__item" :aria-label="`定位${alarm.vehicleNo || alarm.waybillNo}并查看${alarmTypeText(alarm.alarmType)}详情`" @click="focusRecentAlarm(alarm)">
              <div><strong>{{ alarm.vehicleNo || alarm.waybillNo || '-' }}</strong><el-tag size="mini" :type="handleStatusTag(alarm.handleStatus)">{{ handleStatusLabel(alarm.handleStatus) }}</el-tag></div>
              <p>{{ alarmTypeText(alarm.alarmType) }} · {{ alarm.detail || alarm.address || '-' }}</p>
              <small>{{ formatTime(alarm.createdAt) }}</small>
            </button>
            <el-empty v-if="!alarmSummaryLoading && !recentAlarms.length" :image-size="54" description="近24小时暂无此类告警" />
          </div>
        </section>
        <div class="map-alarm-board__categories">
          <p class="map-alarm-board__caption">近24小时<br /><small>未处理 / 总数</small></p>
          <button v-for="category in alarmCategories" :key="category.value" class="map-alarm-board__category" :class="{ 'is-active': alarmListVisible && alarmCategory === category.value }" type="button" :aria-label="`查看${category.label}告警`" @mouseenter="selectAlarmCategory(category.value)" @focus="selectAlarmCategory(category.value)" @click="selectAlarmCategory(category.value)">
            <strong>{{ category.label }}</strong><span v-if="alarmCount(category.value)"><em>{{ alarmCount(category.value).unhandled }}</em> / {{ alarmCount(category.value).total }}</span><span v-else>— / —</span>
          </button>
          <div v-if="alarmSummaryError" class="map-alarm-board__error" role="alert">{{ alarmSummaryError }}<button type="button" @click="fetchAlarmSummary">重试</button></div>
          <span v-else-if="alarmSummaryLoading" class="map-alarm-board__loading"><i class="el-icon-loading" /> 更新中</span>
        </div>
      </div>
    </section>

    <div ref="mapToolsEl" class="map-tools" :class="{ 'map-tools--embed': embedMode }">
      <div class="map-tools__items">
        <button type="button" class="map-tool-item" :class="{ 'is-active': satelliteVisible }" :aria-label="satelliteVisible ? '切换普通地图' : '切换卫星地图'" :title="satelliteVisible ? '切换普通地图' : '切换卫星地图'" @click="toggleSatellite"><img src="@/assets/weixing.png" alt="" /><span>{{ satelliteVisible ? '普通' : '卫星' }}</span></button>
        <button type="button" class="map-tool-item" :class="{ 'is-active': measuring }" :aria-label="measuring ? '结束测距' : '开始测距'" :title="measuring ? '结束测距' : '开始测距'" @click="toggleMeasure"><img src="@/assets/ceju.png" alt="" /><span>测距</span></button>
        <button type="button" class="map-tool-item" :class="{ 'is-active': areaDrawing }" :aria-label="areaDrawing ? '取消区域查车' : '区域查车'" :title="areaDrawing ? '取消区域查车' : '区域查车'" @click="toggleAreaSearch"><i class="el-icon-crop" /><span>{{ areaDrawing ? '绘制中' : '区域查车' }}</span></button>
        <button type="button" class="map-tool-item" aria-label="显示全局车辆" title="全局" @click="showGlobal"><img src="@/assets/quanju.png" alt="" /><span>全局</span></button>
        <button type="button" class="map-tool-item" :aria-label="fullscreen ? '退出全屏' : '进入全屏'" :title="fullscreen ? '退出全屏' : '进入全屏'" @click="toggleFullscreen"><img src="@/assets/quanping.png" alt="" /><span>全屏</span></button>
      </div>
      <div class="map-tools__zoom">
        <button type="button" title="放大" aria-label="放大地图" @click="zoomIn"><i class="el-icon-plus" /></button>
        <button type="button" title="缩小" aria-label="缩小地图" @click="zoomOut"><i class="el-icon-minus" /></button>
      </div>
    </div>

    <transition name="el-fade-in-linear">
      <section v-if="areaResultVisible" class="map-area-card">
        <header class="map-area-card__header">
          <div><strong>区域查车</strong><span>共 {{ areaVehicles.length }} 辆</span></div>
          <i class="el-icon-close" @click="clearAreaSearch" />
        </header>
        <el-table
          v-loading="areaSearching"
          :data="areaVehicles"
          height="280"
          size="mini"
          stripe
          @row-click="focusAreaVehicle"
        >
          <el-table-column prop="vehicleNo" label="车牌号" width="100" show-overflow-tooltip />
          <el-table-column label="车牌颜色" width="76">
            <template slot-scope="{ row }">{{ plateColorText(row.plateColor) }}</template>
          </el-table-column>
          <el-table-column prop="currentAddress" label="当前位置" min-width="150" show-overflow-tooltip />
          <el-table-column label="定位时间" width="140">
            <template slot-scope="{ row }">{{ formatTime(row.locationTime) }}</template>
          </el-table-column>
        </el-table>
        <footer><el-button size="mini" @click="clearAreaSearch">清除框选</el-button></footer>
      </section>
    </transition>

    <transition name="el-fade-in-linear">
      <div v-if="filterVisible" class="map-filter-card">
        <div class="map-filter-card__title">地图筛选 <i class="el-icon-close" @click="filterVisible = false" /></div>
        <div class="map-filter-card__label">运输状态</div>
        <el-checkbox-group v-model="filters.transportStatus" class="map-filter-card__checks" @change="applyFilter">
          <el-checkbox v-for="item in transportStatusOptions" :key="item.value" :label="item.value">{{ item.label }}</el-checkbox>
        </el-checkbox-group>
        <template v-if="filters.transportStatus.some(status => status !== '1')">
          <div class="map-filter-card__label">历史运单创建时间</div>
          <el-date-picker v-model="filters.historyRange" type="datetimerange" value-format="timestamp" size="mini" range-separator="至" start-placeholder="开始时间" end-placeholder="结束时间" style="width:100%" @change="applyFilter" />
          <p class="map-filter-card__hint">时间只限制非运输中运单，运输中始终纳入；默认本周。</p>
        </template>
        <div class="map-filter-card__label">监控状态</div>
        <el-radio-group v-model="filters.monitorStatus" class="map-filter-card__radios" @change="applyFilter">
          <el-radio label="ALL">全部</el-radio>
          <el-radio label="ONLINE">在线</el-radio>
          <el-radio label="OFFLINE">离线</el-radio>
          <el-radio label="NO_TRACK">无轨迹</el-radio>
          <el-radio label="STOPPED">已停止</el-radio>
        </el-radio-group>
        <div class="map-filter-card__footer">
          <el-button size="small" @click="resetFilter">重置</el-button>
          <el-button size="small" type="primary" @click="applyFilter">确定</el-button>
        </div>
      </div>
    </transition>

    <div style="display:none">
      <!-- 信息窗接管此节点的位置，保留根节点供Vue持续更新内容。 -->
      <section v-show="drawerVisible && selectedRow" ref="waybillInfoEl" class="map-waybill-window" aria-label="车辆运单信息" @click.stop @mousedown.stop>
        <template v-if="selectedRow">
          <button type="button" class="map-waybill-window__close" aria-label="关闭运单信息" @click="closeDrawer"><i class="el-icon-close" /></button>
          <header class="map-waybill-window__header">
            <div><strong>{{ selectedRow.vehicleNo || selectedRow.waybillNo }}</strong><span>{{ plateColorText(selectedRow.plateColor) }}</span><small :class="`is-${monitorStatus(selectedLocationRow || selectedRow).toLowerCase()}`">{{ monitorVehicleState }}</small></div>
            <b>{{ formatSpeed(currentLocation && currentLocation.speed) }}</b>
          </header>
          <div class="map-waybill-window__location"><p :title="currentLocationText">{{ currentLocationText }}</p><small>定位时间：{{ formatTime(currentLocation && currentLocation.locationTime) }}{{ currentAddressSourceText }}</small></div>
          <div class="map-waybill-window__orders">
            <button v-if="selectedVehicleWaybills.length > 1" type="button" aria-label="向前滚动运单标签" @click="scrollWaybillTabs(-1)"><i class="el-icon-arrow-left" /></button>
            <div ref="waybillTabsEl" class="map-waybill-window__tabs" role="tablist" aria-label="同车关联运单">
              <button v-for="waybill in selectedVehicleWaybills" :key="waybill.waybillNo" type="button" role="tab" :aria-label="`查看运单${waybill.waybillNo}`" :aria-selected="String(waybill.waybillNo === selectedRow.waybillNo)" :class="{ 'is-active': waybill.waybillNo === selectedRow.waybillNo }" @click="changeSelectedWaybill(waybill.waybillNo)">{{ waybill.waybillNo }}</button>
            </div>
            <button v-if="selectedVehicleWaybills.length > 1" type="button" aria-label="向后滚动运单标签" @click="scrollWaybillTabs(1)"><i class="el-icon-arrow-right" /></button>
          </div>
          <div class="map-waybill-window__body" :style="{ maxHeight: `${vehicleInfoBodyHeight}px` }" :aria-busy="String(detailLoading)">
            <p v-if="detailLoading" class="map-waybill-window__notice"><i class="el-icon-loading" /> 运单信息加载中</p>
            <p v-if="detailError" class="map-waybill-window__error" role="alert">{{ detailError }} <button type="button" @click="selectWaybill(selectedRow, false)">重试</button></p>
            <div class="map-waybill-window__stops">
              <div class="map-waybill-window__stop" :class="{ 'is-pending': Number(selectedRow.transportStatus) === 0 }"><em>起</em><div><p>{{ addressText(selectedWaybill, 'start') }}</p><small>计划出发：{{ formatTime(selectedWaybill.planDepartTime || selectedWaybill.planStartTime) }}</small></div></div>
              <div v-for="stopover in selectedStopovers" :key="stopover.id || stopover.stopoverSeq" class="map-waybill-window__stop"><em>经</em><div><p>{{ addressText(stopover, 'stopover') }}</p><small>计划到达：{{ formatTime(stopover.planArriveTime) }}</small></div></div>
              <div class="map-waybill-window__stop map-waybill-window__stop--end" :class="{ 'is-pending': Number(selectedRow.transportStatus) !== 2 }"><em>终</em><div><p>{{ addressText(selectedWaybill, 'end') }}</p><small>计划到达：{{ formatTime(selectedWaybill.planArriveTime) }}</small></div></div>
            </div>
            <div class="map-waybill-window__extra">
              <p v-if="selectedWaybill.driverName || selectedWaybill.driverPhone"><span>司机：</span>{{ driverText }}</p>
              <p v-if="selectedWaybill.cargoName"><span>货物：</span>{{ selectedWaybill.cargoName }}</p>
              <p v-if="selectedWaybill.remark" :title="selectedWaybill.remark"><span>备注：</span>{{ selectedWaybill.remark }}</p>
            </div>
            <div class="map-waybill-window__monitor">
              <el-tag size="mini" :type="transportStatusTag(selectedRow.transportStatus)">{{ transportStatusText(selectedRow.transportStatus) }}</el-tag>
              <template v-if="![2, 3, 4].includes(Number(selectedRow.transportStatus))"><p>预计到达：<b>{{ formatTime(selectedMonitor && selectedMonitor.estimatedArriveTime) }}</b>{{ estimatedArriveSourceText }}</p><p>剩余里程：<b>{{ formatDistance(selectedMonitor && selectedMonitor.remainingDistance) }}</b>{{ remainingDistanceSourceText }}</p></template>
              <p v-else-if="selectedLocationRow && selectedLocationRow.waybillNo === selectedRow.waybillNo">历史运单显示该单最后一次有效定位</p>
            </div>
          </div>
          <footer class="map-waybill-window__footer"><button type="button" @click="openWaybillMap('position')">车辆追踪</button><button type="button" @click="openWaybillMap('track')">轨迹回放</button></footer>
        </template>
      </section>
    </div>

    <right-data-bicycle
      class="full-map-bicycle-table"
      :is-collapsed="true"
      :is-expanded-num="vehiclePanelExpandedNum"
      :run-route="multiVehicleRows"
      waybill-mode
      @collapse="handleVehiclePanelCollapse"
      @focus-row-on-map="focusBicycleVehicleRow"
    />

    <div class="map-legend" :class="{ 'map-legend--with-table': vehiclePanelExpandedNum !== 3 }">
      <span><i class="map-legend__dot map-legend__dot--online" />在线</span>
      <span><i class="map-legend__dot map-legend__dot--offline" />离线</span>
      <span><i class="map-legend__dot map-legend__dot--notrack" />无轨迹</span>
      <span><i class="map-legend__dot map-legend__dot--completed" />已完成</span>
      <span><i class="map-legend__line" />点击车辆查看运单</span>
    </div>
    <alarm-rule-dialog v-if="!embedMode && canConfigureAlarms" ref="alarmRuleDialog" />
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { getAlarmList, getFullMapAlarmSummary, getMapOverview, getWaybillDetail, isRequestSuccess } from './lingdongApi'
import { handleStatusLabel, handleStatusTag } from './alarmStatus'
import RightDataBicycle from '@/components/RightDataBicycle.vue'
import AlarmRuleDialog from './AlarmRuleDialog.vue'
import { loadAmapSDK, setAmapSecurityConfig } from '@/utils/amapConfig'
import { gcj02ToWgs84Point, wgs84LonLatToGcj02Point } from './coordinate'
import { buildBicycleMarkerContent } from '@/utils/truckLastLocation'
import markerIconLv from '@/assets/lv.png'
import markerIconRed from '@/assets/red.png'
import markerIconHui from '@/assets/hui.png'
import { loadWaybillPreferences } from './waybillPreferences'
import { hasPermission } from '@/utils/auth'

const DEFAULT_CENTER = [108.946, 34.264]
const defaultFilters = () => {
  const now = Date.now()
  const monday = new Date(now + 8 * 3600000)
  monday.setUTCHours(0, 0, 0, 0)
  monday.setUTCDate(monday.getUTCDate() - (monday.getUTCDay() + 6) % 7)
  return { transportStatus: ['1'], monitorStatus: 'ALL', historyRange: [monday.getTime() - 8 * 3600000, now] }
}
const transportStatusOptions = [
  { value: '0', label: '待开始' },
  { value: '1', label: '运输中' },
  { value: '2', label: '已完成' },
  { value: '3', label: '已作废' },
  { value: '4', label: '运单监控结束' }
]
const alarmTypeMap = {
  1: '晚发', 2: '晚到', 3: '线路偏离', 4: '超时停车', 5: '在途离线',
  6: '进入起始地围栏', 7: '驶出起始地围栏', 8: '进入目的地围栏', 9: '驶出目的地围栏',
  B: '进入经停点', C: '驶出经停点', D: '进入途中围栏', E: '驶出途中围栏',
  F: '途中围栏停车', G: '途中围栏驻留', H: '超速驾驶', I: '疲劳驾驶', J: '疲劳驾驶', 10: '灾害天气'
}
const alarmCategories = [{ value: 'ALL', label: '全部' }, { value: '4', label: '超时停车' }, { value: '1', label: '晚发' }, { value: '2', label: '晚到' }, { value: '3', label: '线路偏离' }]

export default {
  name: 'FullMap',
  components: { RightDataBicycle, AlarmRuleDialog },
  props: {
    embedMode: { type: Boolean, default: false }
  },
  data() {
    const filters = defaultFilters()
    return {
      loading: false,
      overviewError: '',
      overviewRequestId: 0,
      detailRequestId: 0,
      pageDisposed: false,
      hideMapVehicles: false,
      preferenceKey: '',
      map: null,
      infoWindow: null,
      vehicleInfoWindow: null,
      vehicleInfoPoint: null,
      vehicleInfoBodyHeight: 360,
      ruler: null,
      mouseTool: null,
      markerCluster: null,
      tileLayer: { satellite: null, roadNet: null },
      satelliteVisible: false,
      measuring: false,
      areaDrawing: false,
      areaSearching: false,
      areaRectangle: null,
      areaBounds: null,
      areaVehicles: [],
      areaResultVisible: false,
      fullscreen: false,
      allWaybills: [],
      monitorDataMap: {},
      keyword: '',
      appliedKeyword: '',
      appliedSearchField: 'vehicleNo',
      searchField: 'vehicleNo',
      filterVisible: false,
      filters,
      appliedFilters: { ...filters, transportStatus: [...filters.transportStatus], historyRange: [...filters.historyRange] },
      statisticFilter: 'running',
      drawerVisible: false,
      drawerTab: 'waybill',
      selectedRow: null,
      selectedDetail: null,
      detailLoading: false,
      detailError: '',
      selectedAlarms: [],
      alarmLoading: false,
      alarmSummary: null,
      alarmSummaryCategory: '',
      alarmSummaryLoading: false,
      alarmSummaryError: '',
      alarmSummaryRequestId: 0,
      alarmCategory: 'ALL',
      alarmBoardExpanded: true,
      alarmListVisible: false,
      alarmMarker: null,
      alarmInfoVisible: false,
      alarmWaybillRequest: 0,
      alarmDrawerWaybillNo: '',
      alarmCategories,
      selectedVehicleKey: '',
      refreshTimer: null,
      vehiclePanelExpandedNum: 3,
      transportStatusOptions
    }
  },
  computed: {
    canConfigureAlarms() {
      return hasPermission('alarm:config')
    },
    statistics() {
      const runningRows = this.mapRows.filter(item => Number(item.transportStatus) === 1)
      return {
        total: this.mapRows.length,
        pending: this.mapRows.filter(item => Number(item.transportStatus) === 0).length,
        running: runningRows.length,
        offline: runningRows.filter(item => this.monitorStatus(item) === 'OFFLINE').length,
        online: runningRows.filter(item => this.monitorStatus(item) === 'ONLINE').length
      }
    },
    vehicleStatistics() {
      const result = { total: this.vehicleGroups.length, moving: 0, stationary: 0, offline: 0, unknown: 0 }
      this.vehicleGroups.forEach(group => {
        const row = group.markerRow
        const status = this.monitorStatus(row)
        const location = this.monitorLocation(row)
        if (status === 'OFFLINE') result.offline += 1
        else if (status === 'ONLINE' && this.rowPoint(row) && location && location.speed != null && location.speed !== '' && Number.isFinite(Number(location.speed)) && Number(location.speed) >= 0) {
          if (Number(location.speed) > 0) result.moving += 1
          else result.stationary += 1
        } else result.unknown += 1
      })
      return result
    },
    recentAlarms() {
      return this.alarmSummaryCategory === this.alarmCategory && this.alarmSummary && Array.isArray(this.alarmSummary.recentAlarms) ? this.alarmSummary.recentAlarms : []
    },
    alarmCategoryLabel() {
      const category = this.alarmCategories.find(item => item.value === this.alarmCategory)
      return `${category ? category.label : '全部'}告警`
    },
    mapRows() {
      const keyword = this.appliedKeyword.toLowerCase()
      return this.allWaybills.filter((item) => {
        if (!this.appliedFilters.transportStatus.includes(String(item.transportStatus))) return false
        if (this.appliedFilters.monitorStatus !== 'ALL' && this.monitorStatus(item) !== this.appliedFilters.monitorStatus) return false
        if (!keyword) return true
        return String(item[this.appliedSearchField] || '').toLowerCase().includes(keyword)
      })
    },
    vehicleGroups() {
      const groups = new Map()
      this.mapRows.forEach((row) => {
        const key = this.vehicleKey(row)
        if (!groups.has(key)) groups.set(key, { key, vehicleNo: String(row.vehicleNo || '').trim() || row.waybillNo, waybills: [] })
        groups.get(key).waybills.push(row)
      })
      return Array.from(groups.values()).map(group => {
        const selected = this.selectedRow && group.waybills.find(row => row.waybillNo === this.selectedRow.waybillNo)
        return { ...group, markerRow: selected && this.rowPoint(selected) ? selected : this.latestLocatedWaybill(group.waybills) }
      })
    },
    multiVehicleRows() {
      return this.mapRows.map((row) => {
        const location = this.monitorLocation(row) || {}
        const point = this.rowPoint(row)
        return {
          waybillNo: row.waybillNo,
          vclN: row.vehicleNo || row.waybillNo,
          plateColor: row.plateColor,
          routeName: row.routeName,
          startUnit: this.addressText(row, 'start'),
          endUnit: this.addressText(row, 'end'),
          totalMileage: row.totalMileage,
          remainingMileage: location.remainingDistance != null ? location.remainingDistance : row.remainingMileage,
          estimatedArriveTime: location.estimatedArriveTime,
          lon: point ? point[0] : null,
          lat: point ? point[1] : null,
          gtm: this.formatTime(location.locationTime),
          spd: location.speed,
          drc: location.direction,
          agl: location.direction,
          mil: location.mileage,
          adr: location.locationName,
          sts: this.monitorText(row)
        }
      })
    },
    selectedWaybill() {
      return this.selectedDetail && this.selectedDetail.waybill ? this.selectedDetail.waybill : (this.selectedRow || {})
    },
    selectedVehicleWaybills() {
      if (!this.selectedVehicleKey) return this.selectedRow ? [this.selectedRow] : []
      const group = this.vehicleGroups.find(item => item.key === this.selectedVehicleKey)
      return group ? group.waybills : (this.selectedRow ? [this.selectedRow] : [])
    },
    selectedStopovers() {
      return this.selectedDetail && Array.isArray(this.selectedDetail.stopovers) ? this.selectedDetail.stopovers : []
    },
    selectedMonitorData() {
      if (!this.selectedRow) return null
      return this.monitorDataMap[this.selectedRow.waybillNo] || (this.selectedDetail && this.selectedDetail.monitor ? { monitor: this.selectedDetail.monitor } : null)
    },
    selectedMonitor() {
      return this.selectedMonitorData && this.selectedMonitorData.monitor ? this.selectedMonitorData.monitor : null
    },
    selectedLocationRow() {
      if (!this.selectedRow) return null
      if (this.rowPoint(this.selectedRow)) return this.selectedRow
      const group = this.vehicleGroups.find(item => item.key === this.selectedVehicleKey)
      return group && this.rowPoint(group.markerRow) ? group.markerRow : null
    },
    currentLocation() {
      return this.selectedLocationRow ? this.monitorLocation(this.selectedLocationRow) : null
    },
    currentLocationText() {
      if (!this.currentLocation) return this.monitorText(this.selectedRow)
      const sourceData = this.selectedLocationRow && this.monitorDataMap[this.selectedLocationRow.waybillNo]
      return this.currentLocation.locationName || (sourceData && sourceData.monitor && sourceData.monitor.currentAddress) || `${this.numberText(this.currentLocation.longitude)}, ${this.numberText(this.currentLocation.latitude)}`
    },
    currentAddressSourceText() {
      const row = this.selectedLocationRow
      const borrowed = row && this.selectedRow && row.waybillNo !== this.selectedRow.waybillNo
      const sourceData = row && this.monitorDataMap[row.waybillNo]
      const monitor = borrowed ? sourceData && sourceData.monitor : this.selectedMonitor
      return `${monitor && monitor.currentAddressSource === 'REVERSE_GEOCODE' ? '（坐标解析）' : ''}${borrowed ? `（位置来自运单${row.waybillNo}）` : ''}`
    },
    remainingDistanceSourceText() {
      const source = this.selectedMonitor && this.selectedMonitor.remainingDistanceSource
      return source === 'ROUTE_ESTIMATE' ? '（路线估算）' : (source === 'DIRECT_ESTIMATE' ? '（直线估算）' : '')
    },
    estimatedArriveSourceText() {
      return this.selectedMonitor && this.selectedMonitor.estimatedArriveSource === 'ESTIMATE' ? '（系统估算）' : ''
    },
    driverText() {
      const detail = this.selectedWaybill
      const name = detail.driverName || ''
      const phone = detail.driverPhone || ''
      return name ? `${name}${phone ? `（${phone}）` : ''}` : (phone || '-')
    },
    monitorCurrentState() {
      if (Number(this.selectedRow && this.selectedRow.transportStatus) === 2) return '已完成'
      if (Number(this.selectedRow && this.selectedRow.transportStatus) === 3) return '已作废'
      if (Number(this.selectedRow && this.selectedRow.transportStatus) === 4) return '监控超期'
      if (this.currentLocation) return '已定位'
      return Number(this.selectedRow && this.selectedRow.transportStatus) === 1 ? '等待定位' : '未启动'
    },
    monitorVehicleState() {
      const row = this.selectedLocationRow || this.selectedRow
      if ([2, 3, 4].includes(Number(row && row.transportStatus))) return '已停止监控'
      if (!row) return '-'
      return ({ ONLINE: '在线', OFFLINE: '离线', NO_TRACK: '无定位', STOPPED: '已停止监控' })[this.monitorStatus(row)] || '-'
    },
    monitorOfflineDuration() {
      if ([2, 3, 4].includes(Number(this.selectedRow && this.selectedRow.transportStatus))) return '-'
      if (!this.selectedMonitor || this.monitorStatus(this.selectedRow) !== 'OFFLINE' || this.selectedMonitor.offlineTime == null) return '-'
      return `${this.selectedMonitor.offlineTime} 分钟`
    }
  },
  mounted() {
    this.initPage()
    document.addEventListener('fullscreenchange', this.handleFullscreenChange)
    window.addEventListener('waybill-preferences-changed', this.handleWaybillPreferences)
    window.addEventListener('storage', this.handlePreferenceStorage)
  },
  beforeDestroy() {
    this.pageDisposed = true
    this.overviewRequestId += 1
    this.detailRequestId += 1
    this.alarmSummaryRequestId += 1
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange)
    window.removeEventListener('waybill-preferences-changed', this.handleWaybillPreferences)
    window.removeEventListener('storage', this.handlePreferenceStorage)
    if (this.refreshTimer) window.clearInterval(this.refreshTimer)
    if (this.ruler) this.ruler.turnOff()
    if (this.mouseTool) {
      this.mouseTool.off('draw', this.handleAreaDraw)
      this.mouseTool.close(false)
    }
    if (this.markerCluster) this.markerCluster.setMap(null)
    if (this.tileLayer.satellite) this.tileLayer.satellite.setMap(null)
    if (this.tileLayer.roadNet) this.tileLayer.roadNet.setMap(null)
    if (this.map) this.map.destroy()
  },
  methods: {
    handleWaybillPreferences(event) {
      if (this.embedMode) return
      this.hideMapVehicles = event.detail.hideMapVehicles === true
      if (this.hideMapVehicles && this.infoWindow) { this.alarmInfoVisible = false; this.infoWindow.close() }
      this.renderMarkers(false)
    },
    handlePreferenceStorage(event) {
      if (event.key === this.preferenceKey) this.loadMapPreferences()
    },
    async loadMapPreferences() {
      try {
        const preferences = await loadWaybillPreferences()
        this.preferenceKey = preferences.key
        this.hideMapVehicles = preferences.value.hideMapVehicles
        this.renderMarkers(false)
      } catch (error) { this.$message.warning('地图偏好加载失败，使用默认设置') }
    },
    async initPage() {
      this.loading = true
      try {
        if (!this.embedMode) await this.loadMapPreferences()
        await this.initMap()
        if (this.pageDisposed) return
        this.fetchAlarmSummary()
        this.startRefreshTimer()
        await this.fetchOverview()
      } catch (error) {
        if (!this.pageDisposed) this.$message.error(error.message || '全图可视加载失败')
      } finally {
        if (!this.pageDisposed) this.loading = false
      }
    },
    async initMap() {
      setAmapSecurityConfig()
      await loadAmapSDK(['AMap.RangingTool', 'AMap.MarkerCluster', 'AMap.MouseTool'])
      if (this.pageDisposed) return
      this.map = new window.AMap.Map(this.$refs.mapEl, {
        zoom: 5,
        center: DEFAULT_CENTER,
        resizeEnable: true,
        viewMode: '2D'
      })
      this.infoWindow = new window.AMap.InfoWindow({ offset: new window.AMap.Pixel(0, -26) })
    },
    async fetchOverview(keepView) {
      if (this.pageDisposed) return
      if (!keepView) { this.alarmWaybillRequest++; this.alarmDrawerWaybillNo = '' }
      const id = ++this.overviewRequestId
      const filters = { ...this.filters, transportStatus: [...this.filters.transportStatus], historyRange: [...(this.filters.historyRange || [])] }
      const keyword = String(this.keyword || '').trim()
      const searchField = this.searchField
      this.loading = true
      this.overviewError = ''
      try {
        const query = { statuses: filters.transportStatus, monitorStatus: filters.monitorStatus }
        if (keyword) query[searchField] = keyword
        if (filters.transportStatus.some(status => status !== '1')) {
          const range = filters.historyRange
          if (range.length !== 2 || range.some(value => value == null || value === '' || !Number.isFinite(Number(value))) || Number(range[0]) > Number(range[1])) throw new Error('请选择合法的历史运单创建时间范围')
          query.historyCreatedFrom = new Date(Number(range[0])).toISOString()
          query.historyCreatedTo = new Date(Number(range[1])).toISOString()
        }
        const response = await getMapOverview(query)
        if (id !== this.overviewRequestId || this.pageDisposed) return
        if (!isRequestSuccess(response)) throw new Error(response.msg || '全图概览查询失败')
        const data = response.data || {}
        this.allWaybills = data.waybills || []
        this.appliedFilters = filters
        this.appliedKeyword = keyword
        this.appliedSearchField = searchField
        const monitors = data.monitors || {}
        this.monitorDataMap = this.allWaybills.reduce((result, row) => {
          const waybillNo = row.waybillNo
          const monitor = monitors[waybillNo]
          const previous = this.monitorDataMap[waybillNo]
          const located = monitor && wgs84LonLatToGcj02Point(`${monitor.currentLongitude},${monitor.currentLatitude}`)
          // 概览无定位占位不能抹掉该可见运单已查得的有效位置。
          if (!located && previous && this.rowPoint(row)) result[waybillNo] = previous
          else if (monitor) result[waybillNo] = { monitor }
          return result
        }, {})
        if (this.selectedRow) {
          const selected = this.allWaybills.find(row => row.waybillNo === this.selectedRow.waybillNo)
          if (selected) {
            this.selectedRow = selected
            this.vehicleInfoPoint = this.selectedLocationRow && this.rowPoint(this.selectedLocationRow)
          }
          else if (!keepView || this.alarmDrawerWaybillNo !== this.selectedRow.waybillNo) this.closeDrawer()
        }
        if (this.areaBounds) this.applyAreaBounds()
        this.renderMarkers(!keepView)
      } catch (error) {
        if (id !== this.overviewRequestId || this.pageDisposed) return
        this.overviewError = error.msg || error.message || '全图概览查询失败'
        throw error
      } finally { if (id === this.overviewRequestId) this.loading = false }
    },
    startRefreshTimer() {
      if (this.pageDisposed) return
      if (this.refreshTimer) window.clearInterval(this.refreshTimer)
      this.refreshTimer = window.setInterval(() => {
        this.fetchOverview(true).catch(() => {})
        this.fetchAlarmSummary()
      }, 30000)
    },
    async fetchAlarmSummary() {
      if (this.embedMode || this.pageDisposed) return
      const requestId = ++this.alarmSummaryRequestId
      const category = this.alarmCategory
      this.alarmSummaryLoading = true
      this.alarmSummaryError = ''
      try {
        const response = await getFullMapAlarmSummary({ alarmType: category })
        if (requestId !== this.alarmSummaryRequestId || this.pageDisposed) return
        if (!isRequestSuccess(response) || !response.data || !Array.isArray(response.data.counts) || !Array.isArray(response.data.recentAlarms)) throw new Error(response && response.msg || '告警概览查询失败')
        this.alarmSummary = response.data
        this.alarmSummaryCategory = category
      } catch (error) {
        if (requestId !== this.alarmSummaryRequestId || this.pageDisposed) return
        this.alarmSummaryError = error.msg || error.message || '告警概览查询失败'
      } finally {
        if (requestId === this.alarmSummaryRequestId && !this.pageDisposed) this.alarmSummaryLoading = false
      }
    },
    alarmCount(type) {
      if (this.alarmSummaryError || !this.alarmSummary) return null
      return this.alarmSummary.counts.find(item => item.alarmType === type) || null
    },
    selectAlarmCategory(type) {
      this.areaResultVisible = false
      this.alarmCategory = type
      this.alarmListVisible = true
      if (this.alarmSummaryCategory === type && !this.alarmSummaryError && !this.alarmSummaryLoading) return
      return this.fetchAlarmSummary()
    },
    toggleAlarmBoard() {
      this.alarmBoardExpanded = !this.alarmBoardExpanded
      this.alarmListVisible = false
    },
    showRecentAlarms() {
      if (this.alarmBoardExpanded) return this.selectAlarmCategory(this.alarmCategory)
    },
    focusRecentAlarm(alarm) {
      if (this.embedMode || this.pageDisposed || !this.map) return
      const alarmPoint = wgs84LonLatToGcj02Point(`${alarm.longitude},${alarm.latitude}`)
      const row = this.allWaybills.find(item => item.waybillNo === alarm.waybillNo)
      const point = alarmPoint || (row && this.rowPoint(row))
      if (!point) {
        this.$message.warning('该告警暂无有效定位，无法在地图定位车辆')
        return
      }
      this.closeDrawer()
      this.alarmListVisible = false
      if (this.alarmMarker) this.alarmMarker.setMap(null)
      // 告警坐标是发生时的位置，不能冒充当前车辆定位或写回监控快照。
      const markerContent = buildBicycleMarkerContent({ vclN: alarm.vehicleNo || alarm.waybillNo, spd: alarm.speed }, markerIconRed).root
      markerContent.lastElementChild.style.pointerEvents = 'auto'
      markerContent.lastElementChild.style.cursor = 'pointer'
      this.alarmMarker = new window.AMap.Marker({
        position: point,
        content: markerContent,
        zIndex: 300
      })
      this.alarmMarker.on('click', () => this.openAlarmWaybill(alarm))
      this.alarmMarker.setMap(this.map)
      const lines = [
        ['运单号', alarm.waybillNo || '-'], ['告警类型', this.alarmTypeText(alarm.alarmType)],
        ['发生时间', this.formatTime(alarm.alarmTime || alarm.createdAt)], ['告警内容', alarm.detail || '-'],
        ['告警地址', alarm.address || '-'], ['处理状态', `${this.handleStatusLabel(alarm.handleStatus)}${alarm.falseAlarm ? '（误报）' : ''}`],
        ['定位来源', alarmPoint ? '告警发生位置' : '该运单车辆最后定位']
      ]
      const content = document.createElement('div')
      content.style.position = 'relative'
      content.innerHTML = `<div style="width:300px;box-sizing:border-box;padding:12px 14px;background:#fff;border-radius:8px;box-shadow:0 2px 12px rgba(0,0,0,.18);font-size:12px;line-height:1.5"><strong style="display:block;padding-right:16px;font-size:16px">${this.escapeHtml(alarm.vehicleNo || alarm.waybillNo)}</strong>${lines.map(([label, value]) => `<p style="margin:4px 0;overflow-wrap:anywhere"><span style="color:#909399">${label}：</span>${label === '运单号' ? `<button type="button" data-alarm-waybill aria-label="查看运单信息" style="border:0;padding:0;background:transparent;color:#1677ff;cursor:pointer;font:inherit">${this.escapeHtml(value)}</button>` : this.escapeHtml(value)}</p>`).join('')}</div>`
      content.querySelector('[data-alarm-waybill]').addEventListener('click', event => {
        event.stopPropagation()
        return this.openAlarmWaybill(alarm)
      })
      const closeButton = document.createElement('button')
      closeButton.type = 'button'
      closeButton.setAttribute('aria-label', '关闭告警详情')
      closeButton.textContent = '×'
      closeButton.style.cssText = 'position:absolute;right:6px;top:4px;border:0;background:transparent;color:#909399;font-size:20px;cursor:pointer'
      closeButton.addEventListener('click', event => {
        event.stopPropagation()
        this.alarmInfoVisible = false
        this.alarmWaybillRequest++
        this.infoWindow.close()
      })
      content.appendChild(closeButton)
      this.infoWindow = new window.AMap.InfoWindow({
        isCustom: true,
        content,
        anchor: 'bottom-center',
        offset: new window.AMap.Pixel(0, -26),
        autoMove: false
      })
      this.map.setZoomAndCenter(14, point, true)
      this.infoWindow.open(this.map, point)
      this.alarmInfoVisible = true
    },
    async openAlarmWaybill(alarm) {
      if (this.embedMode || this.pageDisposed) return
      const waybillNo = alarm && alarm.waybillNo
      if (!waybillNo) { this.$message.warning('该告警未关联运单'); return }
      const requestId = ++this.alarmWaybillRequest
      const detailRequestId = this.detailRequestId
      try {
        const response = await getWaybillDetail({ transportNo: waybillNo })
        if (requestId !== this.alarmWaybillRequest || detailRequestId !== this.detailRequestId || this.pageDisposed) return
        if (!isRequestSuccess(response)) throw new Error(response.msg || '运单详情读取失败')
        if (!response.data || !response.data.waybill || response.data.waybill.waybillNo !== waybillNo) throw new Error('未取得对应运单详情，请重试')
        return this.selectWaybill(response.data.waybill, false, response)
      } catch (error) {
        if (requestId === this.alarmWaybillRequest && detailRequestId === this.detailRequestId && !this.pageDisposed) this.$message.error(error.msg || error.message || '运单详情读取失败')
      }
    },
    toggleVehicleList() {
      this.handleVehiclePanelCollapse(this.vehiclePanelExpandedNum === 3 ? 1 : 3)
    },
    mapViewportPadding() {
      const height = (this.$refs.pageEl && this.$refs.pageEl.clientHeight) || 640
      const statisticsHeight = (this.$refs.statisticsEl && this.$refs.statisticsEl.offsetHeight) || 96
      const statisticsBottom = this.vehiclePanelExpandedNum === 3 ? 18 : 245
      const bottom = this.vehiclePanelExpandedNum === 2 ? height - 100 : statisticsBottom + statisticsHeight + 16
      let right = 30
      if (!this.embedMode) {
        right = ((this.$refs.alarmBoardEl && this.$refs.alarmBoardEl.offsetWidth) || 108) + 38
        if (this.alarmListVisible) right += ((this.$refs.recentAlarmsEl && this.$refs.recentAlarmsEl.offsetWidth) || 310) + 12
      }
      const left = 30
      const top = Math.max(90, ((this.$refs.mapToolsEl && this.$refs.mapToolsEl.offsetHeight) || 44) + 38)
      return [top, right, Math.min(bottom, Math.max(130, height - 160)), left]
    },
    renderMarkers(fitView) {
      if (!this.map) return
      const alarmInfoOpen = this.alarmMarker && this.alarmInfoVisible
      if (this.markerCluster) {
        this.markerCluster.setMap(null)
        this.markerCluster = null
      }
      this.map.clearMap()
      if (this.areaRectangle && this.areaRectangle.setMap) this.areaRectangle.setMap(this.map)
      if (this.alarmMarker) this.alarmMarker.setMap(this.map)
      if (alarmInfoOpen) this.infoWindow.open(this.map, this.alarmMarker.getPosition())
      if (this.hideMapVehicles && !this.embedMode) return
      const markers = []
      this.vehicleGroups.forEach((group) => {
        const row = group.markerRow
        const point = this.drawerVisible && this.selectedVehicleKey === group.key && this.vehicleInfoPoint ? this.vehicleInfoPoint : this.rowPoint(row)
        if (!point) return
        const marker = new window.AMap.Marker({
          position: point,
          content: this.markerContent(row),
          zIndex: this.selectedVehicleKey === group.key ? 200 : 100
        })
        marker.on('click', () => this.selectVehicle(group, false))
        marker.setMap(this.map)
        markers.push(marker)
      })
      if (markers.length > 1 && window.AMap.MarkerCluster) {
        try {
          this.markerCluster = new window.AMap.MarkerCluster(this.map, markers, {
            gridSize: 80,
            maxZoom: 17,
            renderClusterMarker: this.renderClusterMarker
          })
        } catch (error) {
          this.markerCluster = null
        }
      }
      if (fitView && markers.length) {
        this.map.setFitView(markers, false, this.mapViewportPadding())
      }
      if (this.drawerVisible && this.selectedRow && this.vehicleInfoPoint) this.openVehicleInfo(this.selectedRow, this.vehicleInfoPoint)
    },
    async selectWaybill(row, focus, loadedDetail) {
      if (this.pageDisposed || !row) return
      const alarmPoint = loadedDetail && this.alarmMarker ? this.alarmMarker.getPosition() : null
      this.alarmWaybillRequest++
      this.alarmDrawerWaybillNo = loadedDetail ? row.waybillNo : ''
      if (this.alarmMarker) { this.alarmMarker.setMap(null); this.alarmMarker = null }
      this.alarmInfoVisible = false
      const requestId = ++this.detailRequestId
      this.selectedVehicleKey = this.vehicleKey(row)
      this.selectedRow = row
      this.drawerVisible = true
      this.drawerTab = 'waybill'
      this.selectedDetail = loadedDetail ? loadedDetail.data : null
      this.detailLoading = true
      this.detailError = ''
      this.selectedAlarms = []
      this.alarmLoading = true
      if (this.embedMode) this.$emit('vehicle-selected', {
        waybillNo: row.waybillNo,
        vehicleNo: row.vehicleNo
      })
      const point = (this.selectedLocationRow && this.rowPoint(this.selectedLocationRow)) || alarmPoint
      this.vehicleInfoPoint = point
      if (this.infoWindow && this.infoWindow !== this.vehicleInfoWindow) this.infoWindow.close()
      if (!point && this.vehicleInfoWindow) this.vehicleInfoWindow.close()
      if (this.areaBounds) this.applyAreaBounds()
      this.renderMarkers(false)
      if (focus && point && this.map) {
        this.map.setZoomAndCenter(14, point, true)
      }
      const selectedNo = row.waybillNo
      this.$nextTick(() => {
        if (requestId === this.detailRequestId && !this.pageDisposed && this.drawerVisible) this.openVehicleInfo(row, this.vehicleInfoPoint)
      })
      const [detailResponse, alarmResponse] = await Promise.all([
        loadedDetail ? Promise.resolve(loadedDetail) : getWaybillDetail({ transportNo: selectedNo }).catch(() => null),
        getAlarmList({ transportNo: selectedNo, page: 1, limit: 100 }).catch(() => null)
      ])
      if (requestId !== this.detailRequestId || this.pageDisposed || !this.selectedRow || this.selectedRow.waybillNo !== selectedNo) return
      if (detailResponse && isRequestSuccess(detailResponse) && detailResponse.data && detailResponse.data.waybill && detailResponse.data.waybill.waybillNo === selectedNo) {
        this.selectedDetail = detailResponse.data
        const monitor = detailResponse.data.monitor
        // 详情补齐的有效位置按运单保留，切换同车标签后仍能用作车辆位置。
        if (monitor && wgs84LonLatToGcj02Point(`${monitor.currentLongitude},${monitor.currentLatitude}`) && this.mapRows.some(item => item.waybillNo === selectedNo)) {
          this.$set(this.monitorDataMap, selectedNo, { ...(this.monitorDataMap[selectedNo] || {}), monitor })
        }
      } else {
        this.selectedDetail = null
        this.detailError = '运单详情读取失败，请重试'
      }
      this.detailLoading = false
      this.vehicleInfoPoint = (this.selectedLocationRow && this.rowPoint(this.selectedLocationRow)) || alarmPoint
      if (!this.vehicleInfoPoint) {
        if (this.vehicleInfoWindow) this.vehicleInfoWindow.close()
        this.$message.warning('该运单暂无有效定位，无法在地图显示运单信息')
      }
      if (this.areaBounds) this.applyAreaBounds()
      this.renderMarkers(false)
      const alarms = alarmResponse && isRequestSuccess(alarmResponse) ? (alarmResponse.data || {}) : {}
      this.selectedAlarms = alarms.items || []
      this.alarmLoading = false
      this.$nextTick(() => {
        if (requestId === this.detailRequestId && !this.pageDisposed && this.drawerVisible) this.openVehicleInfo(this.selectedRow, this.vehicleInfoPoint)
      })
    },
    selectVehicle(group, focus) {
      if (!group || !group.waybills || !group.waybills.length) return
      const current = this.selectedRow && this.vehicleKey(this.selectedRow) === group.key
        ? group.waybills.find(item => item.waybillNo === this.selectedRow.waybillNo)
        : null
      this.selectedVehicleKey = group.key
      return this.selectWaybill(current || group.markerRow || group.waybills[0], focus)
    },
    changeSelectedWaybill(waybillNo) {
      const row = this.selectedVehicleWaybills.find(item => item.waybillNo === waybillNo)
      if (row) return this.selectWaybill(row, false)
    },
    scrollWaybillTabs(direction) {
      const tabs = this.$refs.waybillTabsEl
      if (tabs) tabs.scrollLeft += direction * 180
    },
    closeDrawer() {
      this.alarmWaybillRequest++
      this.alarmDrawerWaybillNo = ''
      this.alarmInfoVisible = false
      this.detailRequestId += 1
      this.drawerVisible = false
      this.vehicleInfoPoint = null
      this.selectedVehicleKey = ''
      this.selectedRow = null
      this.selectedDetail = null
      this.detailLoading = false
      this.detailError = ''
      this.selectedAlarms = []
      if (this.infoWindow) this.infoWindow.close()
      this.vehicleInfoWindow = null
      if (this.areaBounds) this.applyAreaBounds()
      this.renderMarkers(false)
    },
    rowPoint(row) {
      const location = this.monitorLocation(row)
      if (!location || location.longitude === null || location.longitude === undefined || location.latitude === null || location.latitude === undefined) return null
      return wgs84LonLatToGcj02Point(`${location.longitude},${location.latitude}`)
    },
    vehicleKey(row) {
      const vehicleNo = String((row && row.vehicleNo) || '').replace(/\s+/g, '').toUpperCase()
      const color = row && row.plateColor
      const colorKey = color === null || color === undefined || String(color).trim() === '' ? 'UNKNOWN' : String(Number(color))
      return vehicleNo ? `${vehicleNo}|${colorKey}` : `WAYBILL:${(row && (row.waybillNo || row.id)) || ''}`
    },
    latestLocatedWaybill(rows) {
      const locatedRows = (rows || []).filter(row => this.rowPoint(row))
      if (!locatedRows.length) return rows && rows.length ? rows[0] : null
      const runningRows = locatedRows.filter(row => Number(row.transportStatus) === 1)
      return (runningRows.length ? runningRows : locatedRows).reduce((latest, row) => {
        const latestLocation = this.monitorLocation(latest) || {}
        const currentLocation = this.monitorLocation(row) || {}
        const latestTime = latestLocation.locationTime ? dayjs(latestLocation.locationTime).valueOf() : 0
        const currentTime = currentLocation.locationTime ? dayjs(currentLocation.locationTime).valueOf() : 0
        return currentTime > latestTime ? row : latest
      })
    },
    monitorLocation(row) {
      const detail = this.selectedDetail
      const data = this.monitorDataMap[row.waybillNo] || (detail && detail.waybill && detail.waybill.waybillNo === row.waybillNo && detail.monitor ? { monitor: detail.monitor } : null)
      if (!data) return null
      if (data.currentTrack && wgs84LonLatToGcj02Point(`${data.currentTrack.longitude},${data.currentTrack.latitude}`)) return data.currentTrack
      let monitor = data.monitor
      if ((!monitor || !wgs84LonLatToGcj02Point(`${monitor.currentLongitude},${monitor.currentLatitude}`)) && detail && detail.waybill && detail.waybill.waybillNo === row.waybillNo && detail.monitor) monitor = detail.monitor
      if (monitor) return { longitude: monitor.currentLongitude, latitude: monitor.currentLatitude, locationTime: monitor.locationTime, locationName: monitor.currentAddress, speed: monitor.currentSpeed, direction: monitor.currentDirection, mileage: monitor.currentMileage, remainingDistance: monitor.remainingDistance, estimatedArriveTime: monitor.estimatedArriveTime }
      return null
    },
    monitorStatus(row) {
      if ([2, 3, 4].includes(Number(row.transportStatus))) return 'STOPPED'
      const monitor = this.monitorDataMap[row.waybillNo] && this.monitorDataMap[row.waybillNo].monitor
      return String((monitor && monitor.monitorStatus) || row.monitorStatus || 'NO_TRACK').toUpperCase()
    },
    monitorText(row) {
      if (Number(row.transportStatus) === 2) return '已完成'
      if (Number(row.transportStatus) === 3) return '已作废'
      if (Number(row.transportStatus) === 4) return '监控超期'
      const text = { ONLINE: '在线', OFFLINE: '离线', NO_TRACK: '无轨迹', STOPPED: '已停止' }
      return text[this.monitorStatus(row)] || '监控中'
    },
    monitorTagType(row) {
      if (Number(row.transportStatus) === 2) return 'success'
      if (Number(row.transportStatus) === 4) return 'info'
      const type = this.monitorStatus(row)
      return type === 'ONLINE' ? 'success' : (type === 'OFFLINE' ? 'danger' : 'info')
    },
    markerContent(row) {
      const status = Number(row.transportStatus) === 2 ? 'completed' : this.monitorStatus(row).toLowerCase()
      const location = this.monitorLocation(row) || {}
      const icon = status === 'online' ? markerIconLv : (status === 'offline' ? markerIconRed : markerIconHui)
      const marker = buildBicycleMarkerContent({
        vclN: row.vehicleNo || row.waybillNo,
        spd: location.speed,
        drc: location.direction,
        agl: location.direction
      }, icon)
      marker.root.lastElementChild.style.pointerEvents = 'auto'
      marker.root.lastElementChild.style.cursor = 'pointer'
      if (status === 'completed') {
        const image = marker.root.querySelector('img')
        if (image) image.style.filter = 'hue-rotate(150deg) saturate(1.4)'
      }
      return marker.root
    },
    renderClusterMarker(context) {
      const count = context.count
      const size = count < 10 ? 42 : (count < 100 ? 48 : 56)
      const content = document.createElement('div')
      content.className = 'fvm-cluster-marker'
      content.style.width = `${size}px`
      content.style.height = `${size}px`
      content.style.lineHeight = `${size}px`
      content.innerHTML = String(count)
      context.marker.setContent(content)
      context.marker.setOffset(new window.AMap.Pixel(-size / 2, -size / 2))
    },
    openVehicleInfo(row, point) {
      if (!this.map || !point || !this.drawerVisible || this.pageDisposed || this.hideMapVehicles && !this.embedMode) return
      const content = this.$refs.waybillInfoEl
      if (!content) return
      const avoid = this.mapViewportPadding()
      avoid[2] += 50
      const height = this.$refs.pageEl ? this.$refs.pageEl.clientHeight : 640
      this.vehicleInfoBodyHeight = Math.max(32, Math.min(360, height - avoid[0] - avoid[2] - 210))
      if (!this.vehicleInfoWindow) {
        this.vehicleInfoWindow = new window.AMap.InfoWindow({
          isCustom: true,
          content,
          anchor: 'bottom-center',
          offset: new window.AMap.Pixel(25, -40),
          avoid,
          autoMove: true
        })
      }
      this.infoWindow = this.vehicleInfoWindow
      this.infoWindow.setContent(content)
      this.infoWindow.open(this.map, point)
    },
    applySearch() {
      return this.applyFilter()
    },
    applyFilter() {
      return this.fetchOverview().catch(error => { this.$message.error(error.msg || error.message || '查询失败') })
    },
    resetFilter() {
      this.filters = defaultFilters()
      this.statisticFilter = 'running'
      this.applyFilter()
    },
    setStatisticFilter(type) {
      this.statisticFilter = type
      if (type === 'all') this.filters = { ...this.filters, transportStatus: ['0', '1', '2', '3', '4'], monitorStatus: 'ALL' }
      if (type === 'pending') this.filters = { ...this.filters, transportStatus: ['0'], monitorStatus: 'ALL' }
      if (type === 'running') this.filters = { ...this.filters, transportStatus: ['1'], monitorStatus: 'ALL' }
      if (type === 'offline') this.filters = { ...this.filters, transportStatus: ['1'], monitorStatus: 'OFFLINE' }
      if (type === 'online') this.filters = { ...this.filters, transportStatus: ['1'], monitorStatus: 'ONLINE' }
      this.applyFilter()
    },
    toggleMeasure() {
      if (!this.map || !window.AMap) return
      if (!this.measuring) {
        window.AMap.plugin('AMap.RangingTool', () => {
          this.ruler = new window.AMap.RangingTool(this.map)
          this.ruler.turnOn()
          this.measuring = true
        })
      } else {
        if (this.ruler) this.ruler.turnOff()
        this.measuring = false
      }
    },
    toggleAreaSearch() {
      if (!this.map || !window.AMap) return
      if (this.areaDrawing) {
        if (this.mouseTool) this.mouseTool.close(false)
        this.areaDrawing = false
        return
      }
      window.AMap.plugin('AMap.MouseTool', () => {
        if (!this.mouseTool) {
          this.mouseTool = new window.AMap.MouseTool(this.map)
          this.mouseTool.on('draw', this.handleAreaDraw)
        }
        if (this.areaRectangle) this.map.remove(this.areaRectangle)
        this.areaRectangle = null
        this.areaVehicles = []
        this.areaResultVisible = false
        this.mouseTool.rectangle({
          strokeColor: '#3867f3',
          strokeWeight: 2,
          fillColor: '#3867f3',
          fillOpacity: 0.12
        })
        this.areaDrawing = true
      })
    },
    async handleAreaDraw(event) {
      this.areaRectangle = event && event.obj
      this.areaDrawing = false
      if (this.mouseTool) this.mouseTool.close(false)
      const bounds = this.areaRectangle && this.areaRectangle.getBounds()
      if (!bounds) return
      const southWest = bounds.getSouthWest()
      const northEast = bounds.getNorthEast()
      const first = gcj02ToWgs84Point([southWest.getLng(), southWest.getLat()])
      const second = gcj02ToWgs84Point([northEast.getLng(), northEast.getLat()])
      if (!first || !second) {
        this.$message.error('框选坐标转换失败')
        return
      }
      this.areaBounds = { minLongitude: Math.min(first[0], second[0]), minLatitude: Math.min(first[1], second[1]), maxLongitude: Math.max(first[0], second[0]), maxLatitude: Math.max(first[1], second[1]) }
      this.alarmListVisible = false
      this.areaResultVisible = true
      this.applyAreaBounds()
    },
    applyAreaBounds() {
      const bounds = this.areaBounds
      if (!bounds) return
      this.areaVehicles = this.vehicleGroups.map(group => {
        const row = group.markerRow
        const point = row && this.monitorLocation(row)
        if (!point || point.longitude == null || point.latitude == null) return null
        const lon = Number(point.longitude), lat = Number(point.latitude)
        if (!Number.isFinite(lon) || !Number.isFinite(lat) || lon < bounds.minLongitude || lon > bounds.maxLongitude || lat < bounds.minLatitude || lat > bounds.maxLatitude) return null
        return { waybillNo: row.waybillNo, vehicleNo: row.vehicleNo, plateColor: row.plateColor, currentLongitude: lon, currentLatitude: lat, currentAddress: point.locationName, locationTime: point.locationTime, monitorStatus: this.monitorStatus(row) }
      }).filter(Boolean)
    },
    clearAreaSearch() {
      if (this.mouseTool) this.mouseTool.close(false)
      if (this.areaRectangle && this.map) this.map.remove(this.areaRectangle)
      this.areaRectangle = null
      this.areaBounds = null
      this.areaDrawing = false
      this.areaSearching = false
      this.areaVehicles = []
      this.areaResultVisible = false
    },
    focusAreaVehicle(monitor) {
      if (!monitor || !monitor.waybillNo) return
      const row = this.allWaybills.find(item => item.waybillNo === monitor.waybillNo)
      if (row) this.selectWaybill(row, true)
    },
    toggleSatellite() {
      if (!this.map || !window.AMap || !window.AMap.TileLayer) return
      if (this.satelliteVisible) {
        if (this.tileLayer.satellite) this.tileLayer.satellite.setMap(null)
        if (this.tileLayer.roadNet) this.tileLayer.roadNet.setMap(null)
        this.satelliteVisible = false
        return
      }
      if (!this.tileLayer.satellite) this.tileLayer.satellite = new window.AMap.TileLayer.Satellite()
      if (!this.tileLayer.roadNet) this.tileLayer.roadNet = new window.AMap.TileLayer.RoadNet()
      this.tileLayer.satellite.setMap(this.map)
      this.tileLayer.roadNet.setMap(this.map)
      this.satelliteVisible = true
    },
    showGlobal() {
      this.renderMarkers(true)
    },
    zoomIn() {
      if (this.map && this.map.getZoom() < 20) this.map.setZoom(this.map.getZoom() + 1)
    },
    zoomOut() {
      if (this.map && this.map.getZoom() > 3) this.map.setZoom(this.map.getZoom() - 1)
    },
    handleVehiclePanelCollapse(num) {
      this.vehiclePanelExpandedNum = num
      this.$nextTick(() => {
        if (this.map) this.map.resize()
        if (this.drawerVisible) {
          if (this.vehicleInfoWindow) this.vehicleInfoWindow.close()
          this.vehicleInfoWindow = null
          this.renderMarkers(false)
        }
      })
    },
    focusBicycleVehicleRow(item) {
      const raw = item && item._raw ? item._raw : {}
      const waybillNo = raw.waybillNo || item.waybillNo
      const row = this.allWaybills.find(waybill => waybill.waybillNo === waybillNo) || this.mapRows.find(waybill => {
        const point = this.rowPoint(waybill)
        return point && Number(point[0]) === Number(raw.lon) && Number(point[1]) === Number(raw.lat) && this.vehicleKey(waybill) === this.vehicleKey({ vehicleNo: raw.vclN || raw.vno, plateColor: raw.plateColor })
      })
      if (row) this.selectWaybill(row, true)
    },
    async toggleFullscreen() {
      if (!document.fullscreenElement) await this.$refs.pageEl.requestFullscreen()
      else await document.exitFullscreen()
    },
    handleFullscreenChange() {
      this.fullscreen = Boolean(document.fullscreenElement)
      this.$nextTick(() => { if (this.map) this.map.resize() })
    },
    async openAlarmSetting() {
      if (this.embedMode || !this.canConfigureAlarms || this.pageDisposed) return
      // 候选弹窗会挂到body，先退出全屏以免被全屏容器遮住。
      if (document.fullscreenElement) await document.exitFullscreen()
      if (this.pageDisposed || this.embedMode || !this.canConfigureAlarms) return
      return this.$refs.alarmRuleDialog.open()
    },
    openWaybillMap(view) {
      if (!this.selectedRow || !this.selectedRow.waybillNo) return
      if (this.embedMode) {
        this.$emit('open-waybill', this.selectedRow.waybillNo)
        return
      }
      this.$router.push({ name: 'LingDongWaybillMapName', params: { transportNo: this.selectedRow.waybillNo }, query: { view: view === 'position' ? 'position' : 'track' } })
    },
    applyEmbedCommand(command, payload) {
      if (command === 'focusVehicle' && payload.vehicleNo) {
        const vehicleNo = String(payload.vehicleNo).replace(/\s+/g, '').toUpperCase()
        const matches = this.mapRows.filter(row => String(row.vehicleNo || '').replace(/\s+/g, '').toUpperCase() === vehicleNo)
        const current = this.selectedRow && matches.find(row => row.waybillNo === this.selectedRow.waybillNo)
        const target = current || this.latestLocatedWaybill(matches)
        const group = target && this.vehicleGroups.find(item => item.key === this.vehicleKey(target))
        if (group) this.selectVehicle(group, true)
      } else if (command === 'focusWaybill' && payload.waybillNo) {
        const row = this.allWaybills.find(item => item.waybillNo === payload.waybillNo)
        if (row) this.selectWaybill(row, true)
      } else if (command === 'setFilter' && payload && typeof payload === 'object') {
        if (payload.vehicleNo !== undefined) {
          this.searchField = 'vehicleNo'
          this.keyword = payload.vehicleNo
          this.applySearch()
        }
      }
    },
    transportStatusText(value) {
      return ({ 0: '待开始', 1: '运输中', 2: '已完成', 3: '已作废', 4: '运单监控结束' })[Number(value)] || '未知'
    },
    transportStatusTag(value) {
      return ({ 0: 'info', 1: 'success', 2: '', 3: 'danger', 4: 'info' })[Number(value)] || 'info'
    },
    plateColorText(value) {
      if (value === null || value === undefined || String(value).trim() === '') return '未知颜色'
      return ({ 1: '蓝色', 2: '黄色', 3: '黄绿色' })[Number(value)] || String(value)
    },
    handleStatusLabel,
    handleStatusTag,
    alarmTypeText(value) { return alarmTypeMap[value] || '监控事件' },
    addressText(waybill, prefix) {
      const area = waybill[`${prefix}AreaName`] || ''
      const address = waybill[`${prefix}Address`] || ''
      return address.startsWith(area) ? (address || '-') : `${area}${address}`
    },
    formatTime(value) { return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-' },
    formatDistance(value) {
      if (value === null || value === undefined || value === '') return '-'
      const distance = Number(value)
      return Number.isFinite(distance) ? `${distance.toFixed(distance % 1 === 0 ? 0 : 1)} km` : '-'
    },
    formatSpeed(value) {
      if (value === null || value === undefined || value === '') return '-'
      const speed = Number(value)
      return Number.isFinite(speed) ? `${speed.toFixed(speed % 1 === 0 ? 0 : 1)} km/h` : '-'
    },
    numberText(value) { return value === null || value === undefined || value === '' ? '-' : Number(value).toFixed(1) },
    directionText(value) {
      const degree = Number(value)
      if (!Number.isFinite(degree)) return '-'
      const texts = ['正北', '东北', '正东', '东南', '正南', '西南', '正西', '西北']
      return `${texts[Math.round(degree / 45) % 8]} ${Math.round(degree)}°`
    },
    escapeHtml(value) {
      return String(value || '').replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char])
    }
  }
}
</script>

<style scoped>
.full-map-page { position: relative; width: 100%; height: calc(100vh - 64px); min-height: 640px; overflow: hidden; background: #eef2f7; }
.full-map-page--embed { height: 100%; min-height: 0; }
.full-map-page:fullscreen { width: 100vw; height: 100vh; }
.full-map-page__map { width: 100%; height: 100%; }
.map-search-bar, .map-tools, .map-statistics, .map-filter-card, .map-area-card, .map-waybill-drawer, .map-legend, .map-list-toggle, .map-alarm-board, .map-recent-alarms { position: absolute; z-index: 20; }
.map-list-toggle { top: 18px; left: 18px; width: 48px; height: 44px; border: 0; border-radius: 5px; background: #fff; color: #303133; box-shadow: 0 2px 8px rgba(30, 49, 75, .14); font-size: 24px; cursor: pointer; }
.map-list-toggle:hover, .map-list-toggle.is-active { color: #1677ff; background: #f5f8ff; }
.map-search-bar { top: 18px; left: 78px; display: flex; height: 44px; border-radius: 5px; box-shadow: 0 2px 8px rgba(30, 49, 75, .14); background: #fff; }
.map-search-bar__select { width: 92px; }
.map-search-bar__select ::v-deep .el-input__inner { height: 44px; border: 0; border-right: 1px solid #edf0f5; border-radius: 5px 0 0 5px; font-size: 14px; color: #242b3a; }
.map-search-bar__input { width: 180px; }
.map-search-bar__input ::v-deep .el-input__inner { height: 44px; border: 0; border-radius: 0; padding-right: 36px; font-size: 14px; }
.map-search-bar__search { line-height: 44px; margin-right: 11px; color: #aab2c0; font-size: 21px; cursor: pointer; }
.map-search-bar__filter { width: 40px; border: 0; border-left: 1px solid #edf0f5; border-radius: 0 5px 5px 0; background: #fff; color: #556176; font-size: 18px; cursor: pointer; }
.map-search-bar__filter:hover, .map-search-bar__filter.is-active { color: #3867f3; background: #f5f8ff; }
.map-statistics { bottom: 18px; left: 50%; display: flex; flex-direction: column; width: max-content; max-width: calc(100% - 36px); transform: translateX(-50%); overflow: hidden; border-radius: 6px; background: #fff; box-shadow: 0 2px 8px rgba(30, 49, 75, .14); }
.map-statistics__waybills { display: flex; justify-content: center; border-bottom: 1px solid #edf0f5; }
.map-statistics__item { position: relative; display: flex; align-items: center; justify-content: center; gap: 9px; width: 200px; height: 42px; min-width: 0; padding: 0 20px; border: 0; border-right: 1px solid #edf0f5; background: #fff; text-align: left; cursor: pointer; }
.map-statistics__item:last-child { border-right: 0; }
.map-statistics__item:hover, .map-statistics__item.is-active { background: #f7faff; }
.map-statistics__item.is-active::after { position: absolute; bottom: 0; left: 10px; right: 10px; height: 3px; border-radius: 3px; background: #3867f3; content: ''; }
.map-statistics__item span { overflow: hidden; color: #333b49; font-size: 13px; white-space: nowrap; }
.map-statistics__item i { font-size: 20px; color: #667085; }
.map-statistics__item strong { color: #303133; font-size: 18px; font-weight: 500; }
.map-statistics__vehicles { display: flex; align-items: center; justify-content: center; gap: 20px; padding: 10px 20px 5px; color: #454c59; font-size: 14px; }
.map-statistics__vehicles > span { display: inline-flex; align-items: center; gap: 7px; white-space: nowrap; }
.map-statistics__vehicles b { font-weight: 500; }
.map-statistics__dot { width: 8px; height: 8px; border-radius: 50%; }
.map-statistics__dot--moving { background: #0bbb82; }.map-statistics__dot--stationary { background: #1677ff; }.map-statistics__dot--offline { background: #9aa4b2; }
.map-statistics__unknown { color: #909399; font-size: 12px; }.map-statistics__scope { padding-bottom: 5px; color: #909399; font-size: 11px; text-align: center; }
.map-tools { top: 18px; right: 138px; display: flex; flex-direction: column; align-items: flex-end; gap: 10px; }
.map-tools--embed { right: 18px; }
.full-map-page--with-table .map-statistics { bottom: 245px; }
.map-tools__items, .map-tools__zoom { display: flex; align-items: center; height: 44px; padding: 0 10px; box-sizing: border-box; border-radius: 5px; background: #fff; box-shadow: 0 2px 8px rgba(0, 0, 0, .12); }
.map-tools__items { gap: 8px; }
.map-tool-item { display: inline-flex; align-items: center; gap: 6px; padding: 2px 4px; border: 0; border-radius: 4px; background: transparent; color: #303133; cursor: pointer; }
.map-tool-item:hover, .map-tool-item.is-active { background: rgba(22, 119, 255, .06); color: #1677ff; }
.map-tool-item img { width: 18px; height: 18px; object-fit: contain; }.map-tool-item i { font-size: 18px; }.map-tool-item span { font-size: 14px; white-space: nowrap; }
.map-tools__zoom { gap: 8px; }.map-tools__zoom button { width: 24px; height: 24px; padding: 0; border: 0; background: transparent; color: #7c8196; font-size: 24px; font-weight: 700; cursor: pointer; }.map-tools__zoom button:hover { color: #1677ff; }
.map-alarm-board { top: 18px; right: 18px; width: 108px; border-radius: 6px; background: #fff; box-shadow: 0 2px 8px rgba(30, 49, 75, .14); text-align: center; }
.map-alarm-board__content { position: absolute; top: 44px; right: 0; display: flex; align-items: flex-start; gap: 12px; }
.map-alarm-board__categories { width: 108px; flex: 0 0 auto; border-radius: 0 0 6px 6px; background: #fff; box-shadow: 0 2px 8px rgba(30, 49, 75, .14); }
.map-alarm-board__actions { display: flex; height: 44px; border-bottom: 1px solid #edf0f5; }.map-alarm-board__actions button { position: relative; flex: 1; border: 0; border-radius: 6px; background: #fff; color: #303133; font-size: 23px; cursor: pointer; }.map-alarm-board__actions button:hover { color: #1677ff; }
.map-alarm-notice em { position: absolute; top: 3px; right: 2px; min-width: 16px; padding: 0 3px; border-radius: 9px; background: #f56c6c; color: #fff; font-size: 11px; font-style: normal; line-height: 17px; }
.map-alarm-board__caption { margin: 0; padding: 9px 0; border-bottom: 1px solid #edf0f5; color: #606266; font-size: 13px; line-height: 18px; }.map-alarm-board__caption small { color: #909399; font-size: 10px; }
.map-alarm-board__category { display: flex; align-items: center; flex-direction: column; justify-content: center; gap: 5px; width: 100%; height: 66px; border: 0; border-bottom: 1px solid #e5e8ed; background: #fff; color: #303133; cursor: pointer; }.map-alarm-board__category:last-child { border-bottom: 0; border-radius: 0 0 6px 6px; }.map-alarm-board__category:hover, .map-alarm-board__category.is-active { background: #f0f7ff; }.map-alarm-board__category strong { font-size: 16px; }.map-alarm-board__category span { font-size: 17px; }.map-alarm-board__category em { color: #f04438; font-style: normal; }
.map-alarm-board__error { padding: 8px 5px; color: #c45656; font-size: 11px; line-height: 16px; overflow-wrap: anywhere; }.map-alarm-board__error button { display: block; margin: 4px auto 0; border: 0; background: transparent; color: #1677ff; cursor: pointer; }.map-alarm-board__loading { display: block; padding: 5px; color: #909399; font-size: 11px; }
.map-alarm-board__content .map-recent-alarms { position: relative; top: auto; right: auto; bottom: auto; flex: 0 0 auto; margin-top: 68px; height: max(160px, calc(100vh - 334px)); width: 310px; overflow: hidden; border-radius: 6px; background: #fff; box-shadow: 0 4px 18px rgba(30,49,75,.18); text-align: left; }
.map-recent-alarms header { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border-bottom: 1px solid #edf0f5; }.map-recent-alarms header strong { display: block; font-size: 16px; color: #344054; }.map-recent-alarms header small { display: block; margin-top: 5px; color: #909399; font-size: 12px; }.map-recent-alarms header button { border: 0; background: #fff; color: #909399; font-size: 18px; cursor: pointer; }
.map-recent-alarms__body { height: calc(100% - 74px); overflow: auto; padding: 0 14px; }.map-recent-alarms__item { display: block; width: 100%; padding: 13px 0; border: 0; border-bottom: 1px solid #edf0f5; background: #fff; text-align: left; cursor: pointer; }.map-recent-alarms__item:hover, .map-recent-alarms__item:focus-visible { background: #f0f7ff; }.map-recent-alarms__item > div { display: flex; justify-content: space-between; gap: 8px; align-items: center; font-size: 14px; color: #344054; }.map-recent-alarms__item p { color: #667085; font-size: 13px; line-height: 19px; overflow-wrap: anywhere; margin: 7px 0; }.map-recent-alarms__item small { color: #98a2b3; font-size: 12px; }.map-recent-alarms__error { margin: 16px; color: #c45656; font-size: 13px; }
.full-map-page--with-table .map-recent-alarms { height: max(160px, calc(100vh - 439px)); }
.map-filter-card { top: 70px; left: 18px; width: 292px; padding: 17px 18px 14px; border-radius: 8px; background: #fff; box-shadow: 0 8px 25px rgba(25, 47, 79, .2); }
.map-filter-card__title { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; color: #2b3341; font-size: 16px; font-weight: 600; }
.map-filter-card__title i { color: #9aa3b1; cursor: pointer; }
.map-filter-card__label { margin: 14px 0 9px; color: #687386; font-size: 13px; }
.map-filter-card__hint { margin: 8px 0; font-size: 12px; line-height: 1.6; color: #909399; }
.map-query-error { position: absolute; z-index: 21; top: 72px; left: 50%; max-width: 440px; padding: 8px 14px; transform: translateX(-50%); background: #fff2f0; color: #c45656; border-radius: 4px; }
.map-filter-card__checks, .map-filter-card__radios { display: flex; flex-wrap: wrap; gap: 8px 14px; }
.map-filter-card ::v-deep .el-checkbox, .map-filter-card ::v-deep .el-radio { margin-right: 0; }
.map-filter-card__footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; }
.map-area-card { top: 130px; right: 138px; width: 500px; overflow: hidden; border-radius: 8px; background: #fff; box-shadow: 0 8px 25px rgba(25, 47, 79, .2); }
.map-area-card__header { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border-bottom: 1px solid #edf0f5; color: #2b3341; }
.map-area-card__header strong { margin-right: 10px; font-size: 16px; }.map-area-card__header span { color: #3867f3; font-size: 13px; }.map-area-card__header > i { color: #9aa3b1; cursor: pointer; }
.map-area-card ::v-deep .el-table__row { cursor: pointer; }.map-area-card footer { padding: 10px 14px; border-top: 1px solid #edf0f5; text-align: right; }
.map-waybill-window { position: relative; width: 412px; max-width: calc(100vw - 36px); min-height: 156px; border-radius: 5px; background: rgba(255,255,255,.96); box-shadow: 0 4px 12px rgba(0,0,0,.18); color: #303133; font-size: 12px; line-height: 1.5; }
.map-waybill-window::after { position: absolute; left: calc(50% - 9px); bottom: -10px; width: 0; height: 0; border-left: 9px solid transparent; border-right: 9px solid transparent; border-top: 10px solid rgba(255,255,255,.96); content: ''; }
.map-waybill-window button { font: inherit; cursor: pointer; }.map-waybill-window button:focus-visible { outline: 2px solid #1677ff; outline-offset: -2px; }
.map-waybill-window__close { position: absolute; right: 7px; top: 7px; z-index: 1; border: 0; background: transparent; color: #909399; font-size: 17px !important; }
.map-waybill-window__header { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 13px 30px 9px 20px; border-bottom: 1px solid #e3e6ea; }.map-waybill-window__header > div { display: flex; align-items: center; gap: 8px; }.map-waybill-window__header strong { color: #e8a000; font-size: 17px; }.map-waybill-window__header span { color: #909399; }.map-waybill-window__header small { color: #909399; }.map-waybill-window__header .is-online { color: #19b96b; }.map-waybill-window__header b { color: #1677ff; font-size: 16px; white-space: nowrap; }
.map-waybill-window__location { padding: 9px 20px; }.map-waybill-window__location p { margin: 0 0 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }.map-waybill-window__location small { color: #909399; }
.map-waybill-window__orders { display: flex; align-items: stretch; border-top: 1px solid #edf0f5; border-bottom: 1px solid #edf0f5; }.map-waybill-window__orders > button { flex: 0 0 24px; padding: 0; border: 0; background: #f8faff; color: #909399; }.map-waybill-window__tabs { display: flex; flex: 1; min-width: 0; overflow-x: auto; scrollbar-width: none; }.map-waybill-window__tabs::-webkit-scrollbar { display: none; }.map-waybill-window__tabs button { flex: 0 0 auto; max-width: 100%; overflow: hidden; padding: 11px 15px; border: 0; border-bottom: 2px solid transparent; background: transparent; color: #606266; text-overflow: ellipsis; white-space: nowrap; }.map-waybill-window__tabs button.is-active { border-bottom-color: #1677ff; color: #1677ff; font-weight: 600; }
.map-waybill-window__body { max-height: min(360px, calc(100vh - 310px)); overflow-y: auto; }.map-waybill-window__stops { max-height: 220px; overflow-y: auto; margin: 14px 20px 10px; }.map-waybill-window__stop { position: relative; display: flex; gap: 12px; padding-bottom: 14px; }.map-waybill-window__stop:not(:last-child)::after { position: absolute; top: 21px; left: 9px; bottom: 1px; border-left: 1px dashed #ccd2dc; content: ''; }.map-waybill-window__stop em { flex: 0 0 20px; height: 20px; border-radius: 50%; background: #19b96b; color: #fff; font-style: normal; text-align: center; line-height: 20px; }.map-waybill-window__stop--end em { background: #f06464; }.map-waybill-window__stop.is-pending em { background: #b7c0cb; }.map-waybill-window__stop p { margin: 0 0 3px; overflow-wrap: anywhere; }.map-waybill-window__stop small { color: #909399; }
.map-waybill-window__extra { padding: 0 20px; }.map-waybill-window__extra p { margin: 4px 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }.map-waybill-window__extra span { color: #909399; }
.map-waybill-window__monitor { padding: 10px 20px; border-top: 1px solid #edf0f5; }.map-waybill-window__monitor p { margin: 5px 0 0; color: #909399; }.map-waybill-window__monitor b { color: #303133; font-weight: 400; }.map-waybill-window__footer { display: flex; justify-content: space-evenly; padding: 10px 20px; border-top: 1px solid #edf0f5; }.map-waybill-window__footer button { border: 0; background: transparent; color: #1677ff; }
.map-waybill-window__notice, .map-waybill-window__error { margin: 10px 20px; color: #909399; }.map-waybill-window__error { color: #c45656; }.map-waybill-window__error button { border: 0; background: transparent; color: #1677ff; }
.map-waybill-drawer { top: 70px; bottom: 0; left: 0; width: 23rem; overflow: hidden; border-radius: 0 6px 0 0; background: #fff; box-shadow: 0 8px 28px rgba(25, 47, 79, .2); }
.map-waybill-drawer--with-table { bottom: 0; }
.map-waybill-drawer__header { display: flex; align-items: center; justify-content: space-between; height: 83px; padding: 0 24px; background: #f2f5ff; }
.map-waybill-drawer__header strong { margin-right: 12px; color: #3867f3; font-size: 24px; }
.map-waybill-drawer__header > i { color: #c2c9d4; font-size: 22px; cursor: pointer; }
.map-waybill-drawer__selector { height: 72px; padding: 10px 20px; box-sizing: border-box; border-bottom: 1px solid #e8ecf3; background: #fff; }
.map-waybill-drawer__selector > div { display: flex; align-items: center; justify-content: space-between; margin-bottom: 7px; color: #344054; font-size: 13px; }
.map-waybill-drawer__selector em { color: #667085; font-size: 12px; font-style: normal; }
.map-waybill-drawer__selector .el-select { width: 100%; }
.map-waybill-drawer__tabs { height: calc(100% - 83px); }
.map-waybill-drawer__tabs--with-selector { height: calc(100% - 155px); }
.map-waybill-drawer__tabs ::v-deep .el-tabs__header { margin: 0; padding: 13px 14px 0; }
.map-waybill-drawer__tabs ::v-deep .el-tabs__nav-wrap::after { background: #e5e9f1; }
.map-waybill-drawer__tabs ::v-deep .el-tabs__item { height: 50px; line-height: 50px; font-size: 16px; }
.map-waybill-drawer__tabs ::v-deep .el-tabs__content { height: calc(100% - 64px); }
.map-waybill-drawer__tabs ::v-deep .el-tab-pane { height: 100%; }
.map-waybill-drawer__body { height: 100%; padding: 26px 28px; box-sizing: border-box; overflow-y: auto; }
.map-waybill-drawer__section { padding-bottom: 22px; border-bottom: 1px solid #eceff4; }.map-waybill-drawer__section + .map-waybill-drawer__section { padding-top: 22px; }
.map-waybill-drawer__section-title { margin-bottom: 13px; color: #2f3b4c; font-size: 16px; font-weight: 600; }.map-waybill-drawer__section-title::before { display: inline-block; width: 3px; height: 16px; margin-right: 8px; vertical-align: -2px; border-radius: 2px; background: #2f78ed; content: ''; }
.map-waybill-drawer__info-list { display: grid; grid-template-columns: 76px minmax(0, 1fr); margin: 0; row-gap: 11px; font-size: 14px; line-height: 21px; }.map-waybill-drawer__info-list dt { color: #7a8598; }.map-waybill-drawer__info-list dd { margin: 0; color: #344054; overflow-wrap: anywhere; word-break: break-word; }.map-waybill-drawer__info-list--monitor dd { color: #475467; }.map-monitor-source { margin-left: 4px; color: #909399; font-size: 12px; font-style: normal; white-space: nowrap; }
.map-waybill-drawer__track-button { width: 100%; }
.map-waybill-drawer__alarm-list { padding-top: 14px; }
.map-alarm-item { display: flex; gap: 10px; padding: 13px 0; border-bottom: 1px solid #edf0f5; }
.map-alarm-item__dot { width: 8px; height: 8px; margin-top: 7px; border-radius: 50%; flex: 0 0 auto; }
.map-alarm-item__dot--1 { background: #f04438; }.map-alarm-item__dot--2 { background: #f79009; }.map-alarm-item__dot--3 { background: #409eff; }
.map-alarm-item strong { color: #344054; font-size: 14px; }.map-alarm-item p { margin: 5px 0; color: #667085; font-size: 13px; line-height: 19px; }.map-alarm-item small { color: #98a2b3; font-size: 12px; }
.map-waybill-drawer__loading { padding: 28px 0; color: #909399; text-align: center; }
.full-map-bicycle-table { position: absolute; inset: 0; z-index: 19; pointer-events: none; }.full-map-bicycle-table ::v-deep .trip-info-popup { padding: 6px 12px; pointer-events: auto; }.full-map-bicycle-table ::v-deep .expand-controls-fiex { pointer-events: none; }.full-map-bicycle-table ::v-deep .expand-controls-fiex .expand-btn { pointer-events: auto; }.full-map-bicycle-table ::v-deep .bicycle-tab-panel-wrap { background: rgba(255,255,255,.96); }.full-map-bicycle-table ::v-deep .el-table__row { cursor: pointer; }
.map-legend { bottom: 108px; left: 18px; display: flex; gap: 14px; padding: 9px 13px; border-radius: 5px; background: rgba(255,255,255,.96); box-shadow: 0 2px 9px rgba(21, 38, 64, .13); color: #526072; font-size: 12px; transition: bottom .2s ease; }
.map-legend--with-table { bottom: 338px; }
.map-legend--with-drawer { left: calc(23rem + 18px); }
.map-legend span { display: flex; align-items: center; gap: 5px; }.map-legend__dot { width: 10px; height: 10px; border-radius: 50%; }.map-legend__dot--online { background: #17be82; }.map-legend__dot--offline { background: #f0445d; }.map-legend__dot--notrack { background: #9aa4b2; }.map-legend__dot--completed { background: #4d70d9; }.map-legend__line { width: 18px; height: 0; border-top: 2px dashed #3f72f5; }
::v-deep .fvm-cluster-marker { box-sizing: border-box; border: 3px solid rgba(255,255,255,.95); border-radius: 50%; background: #3867f3; box-shadow: 0 2px 8px rgba(29,43,64,.28); color: #fff; font-size: 15px; font-weight: 700; text-align: center; }
.map-drawer-enter-active, .map-drawer-leave-active { transition: transform .22s ease, opacity .22s ease; }.map-drawer-enter, .map-drawer-leave-to { opacity: 0; transform: translateX(-18px); }
@media (max-width: 1500px) { .map-statistics__vehicles { gap: 14px; font-size: 13px; }.map-search-bar__input { width: 180px; } }
@media (max-width: 1200px) { .map-statistics__vehicles { gap: 10px; padding-left: 14px; padding-right: 14px; }.map-tool-item { gap: 4px; padding: 2px; }.map-tool-item span { font-size: 13px; }.map-tool-item img { width: 16px; height: 16px; }.map-search-bar__input { width: 150px; }.map-alarm-board__content .map-recent-alarms { width: 280px; }.map-alarm-board, .map-alarm-board__categories { width: 96px; }.map-area-card, .map-tools { right: 126px; } }
@media (max-width: 1100px) { .map-tool-item span { display: none; } }
@media (max-width: 760px) { .map-search-bar { left: 70px; }.map-search-bar__input { width: 120px; }.map-search-bar__select { width: 82px; }.map-list-toggle { width: 42px; }.map-alarm-board { right: 10px; width: 84px; }.map-alarm-board__categories { width: 84px; }.map-alarm-board__category { height: 55px; }.map-alarm-board__category strong { font-size: 13px; }.map-alarm-board__content .map-recent-alarms { width: min(280px, calc(100vw - 122px)); }.map-statistics__vehicles { flex-wrap: wrap; row-gap: 5px; }.map-statistics__item { width: 150px; }.map-legend { max-width: calc(100% - 160px); flex-wrap: wrap; gap: 5px 10px; }.map-tools__items { gap: 3px; }.map-tool-item span { display: none; }.map-tools { right: 104px; }.map-area-card { right: 104px; width: calc(100% - 122px); } }
.map-tools--embed { right: 18px; }
</style>
